/*
 * Copyright (c) 2023 CalAmp Corp.  All Rights Reserved
 */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import Box from '@material-ui/core/Box';
import Tab from '@material-ui/core/Tab';
import Tabs from '@material-ui/core/Tabs';
import {translate} from '@ion/core/src/locale/locale';
import {Device, GenericObject} from '@ion/core/src/types/entities';
import {isMDTDevice as isValidMDTDevice} from '@ion/core/src/utils/DeviceUtils';
import {ContentLoader} from '@ion/dls/src/components/loading/ContentLoader';
import {useExtendStyles} from '@ion/dls/src/hooks/useExtendStyles';
import {
  deviceDetailSelector,
  devicePermissionsSelector,
  deviceSCISelector,
  devicesViewSelector
} from '../../store/selectors/devices';
import {AlertHistoryTab} from './AlertHistoryTab';
import {DeviceDetailsTab} from './DeviceDetailsTab';
import {DeviceInfoPanel} from './DeviceInfoPanel';
import {EventsTab} from './EventsTab';
import {FilesTab} from './FilesTab';
import {HealthEventsTab} from './HealthEventsTab';
import {IdReportTab} from './IdReportTab';
import {ManufacturingTab} from './ManufacturingTab';
import {SCISettingsTab} from './SCISettingsTab';
import {hasPermission} from './SCISettingsUtils';
import {ServicesTab} from './ServicesTab';
import {ShippingWarrantyTab} from './ShippingWarrantyTab';
import {
  clearSCI,
  updateSCISetting,
  viewAdminDevice
} from './state';
import {DeviceEditorTabs, ViewDeviceEditorProps, viewDeviceStyles} from './ViewDeviceEditor';
import {ViewDeviceTopMenuProps} from './ViewDeviceTopMenu';

export interface BulkViewDeviceEditorProps extends ViewDeviceEditorProps,
  Pick<ViewDeviceTopMenuProps, 'onRefresh'> {
  manufacturingInfo: GenericObject;
  shippingInfo: GenericObject;
  populatedDevice?: Device | Device[];
  currentFileHistory?: GenericObject[];
}

/**
 * Display Bulk View Device Editor on click on view option from grid menu
 */
export const BulkViewDeviceEditor = ({
  onRefresh,
  manufacturingInfo,
  shippingInfo,
  populatedDevice,
  currentFileHistory,
  selectedJobId,
  devicesPermissions,
  Features,
  explicitReportDate = false,
  isToggleSwitchGrid = false,
  isVbuFwEnabled = false,
  isShowColumnChooserIcon = false,
  isCtcAdmin = false,
  isShowRefreshButton = true
}: BulkViewDeviceEditorProps) => {
  const editorStyles                = useExtendStyles(viewDeviceStyles);
  const classes                     = editorStyles();
  const [activeTab, setActiveTab]   = useState(0);
  const dispatch                    = useDispatch();
  const deviceDetail                = useSelector(deviceDetailSelector);
  const [scriptDesc, setScriptDesc] = useState('');
  const {
    assetData,
    result,
    latestIdReport,
    idReportInfo,
    SCIDeviceData,
    isSCIDevice
  } = useSelector(devicesViewSelector);
  const {
    result: SCIResult,
    loading: SCILoading
  } = useSelector(deviceSCISelector);
  const {permissions} = useSelector(devicePermissionsSelector);

  const isLMU55Device = deviceDetail?.type?.title?.toLowerCase()?.includes('lmu55');
  const isMDTDevice   = isValidMDTDevice(deviceDetail);

  useEffect(() => {
    if (hasSCIPermission && SCIResult) {
      dispatch(viewAdminDevice(null, false));
      dispatch(clearSCI());
    }
  }, [SCIResult]);

  const onSubmit = useCallback((changedSettingsFields) => {
    dispatch(updateSCISetting(changedSettingsFields) as any);
  }, []);

  function handleChange(event, newValue) {
    setActiveTab(newValue);
  };

  const SCIRead = useMemo((): boolean => {
    return hasPermission(permissions, 'read', 'deviceCommand');
  }, [permissions]);

  const SCIUpdate = useMemo((): boolean => {
    return hasPermission(permissions, 'update', 'deviceCommand');
  }, [permissions]);

  const hasSCIPermission = useMemo(() => (
    SCIRead || SCIUpdate
  ), [permissions]);

  const tabs = useMemo(() => translate('admin-devices:view.tabs', {returnObjects: true}), []);

  return (
    <>
      <div>
        <DeviceInfoPanel device={populatedDevice}
          deviceDetail={result as Device}
          latestIdReport={latestIdReport}
          isCtcAdmin={isCtcAdmin}
          explicitReportDate={explicitReportDate} />
      </div>
      <div className={classes.contentContainer}>
        <div>
          <Box className={classes.box}>
            <Tabs value={activeTab} onChange={handleChange}>
              <Tab value={DeviceEditorTabs.DETAILS} className={classes.tabStyle} label={tabs.deviceDetails} />
              <Tab value={DeviceEditorTabs.FILES} className={classes.tabStyle} label={tabs.files} />
              {!isMDTDevice && (
                <Tab value={DeviceEditorTabs.SERVICES} className={classes.tabStyle} label={tabs.services} />
              )}
              <Tab value={DeviceEditorTabs.ID_REPORT} className={classes.tabStyle} label={tabs.idReport} />
              {isLMU55Device && (
                <Tab value={DeviceEditorTabs.HEALTH_EVENTS} className={classes.tabStyle} label={tabs.healthEvent} />
              )}
              <Tab value={DeviceEditorTabs.TELEMETRY_EVENTS} className={classes.tabStyle} label={tabs.events} />
              <Tab value={DeviceEditorTabs.ALERT_HISTORY} className={classes.tabStyle} label={tabs.alertHistory} />
              <Tab value={DeviceEditorTabs.MANUFACTURING} className={classes.tabStyle} label={tabs.mfgData} />
              <Tab value={DeviceEditorTabs.SHIPPING} className={classes.tabStyle} label={tabs.shippingWarranty} />
              {hasSCIPermission && SCIDeviceData?.sciDeviceSettings && (
                <Tab value={DeviceEditorTabs.SCI_SETTINGS} className={classes.tabStyle} label={tabs.sciSettings} />
              )}
            </Tabs>
          </Box>
          <>
            <DeviceDetailsTab selectedTab={activeTab}
              deviceDetail={result as Device}
              Features={Features}
              devicesPermissions={devicesPermissions}
              isVbuFwEnabled={isVbuFwEnabled}
              tabIndex={DeviceEditorTabs.DETAILS}
              device={populatedDevice}
              assetData={assetData}
              manufacturingInfo={manufacturingInfo}
              onSuccess={onRefresh}
              scriptDesc={scriptDesc} />
            <FilesTab selectedTab={activeTab}
              tabIndex={DeviceEditorTabs.FILES}
              selectedJobId={selectedJobId}
              device={deviceDetail}
              currentFileHistory={currentFileHistory}
              isToggleSwitchGrid={isToggleSwitchGrid}
              isShowColumnChooserIcon={isShowColumnChooserIcon}
              isShowRefreshButton={isShowRefreshButton}
              setScriptDesc={setScriptDesc} />
            {!isMDTDevice && <ServicesTab selectedTab={activeTab} tabIndex={DeviceEditorTabs.SERVICES} />}
            <IdReportTab selectedTab={activeTab}
              isMDT={isMDTDevice}
              tabIndex={DeviceEditorTabs.ID_REPORT}
              idReportInfo={idReportInfo || undefined} />
            {isLMU55Device && (
              <HealthEventsTab selectedTab={activeTab}
                tabIndex={DeviceEditorTabs.HEALTH_EVENTS}
                device={populatedDevice}/>
            )}
            <EventsTab selectedTab={activeTab} tabIndex={DeviceEditorTabs.TELEMETRY_EVENTS} device={deviceDetail} />
            <AlertHistoryTab selectedTab={activeTab}
              tabIndex={DeviceEditorTabs.ALERT_HISTORY}
              device={populatedDevice} />
            <ManufacturingTab isCMF={isMDTDevice}
              selectedTab={activeTab}
              tabIndex={DeviceEditorTabs.MANUFACTURING}
              manufacturingInfo={manufacturingInfo} />
            <ShippingWarrantyTab selectedTab={activeTab}
              tabIndex={DeviceEditorTabs.SHIPPING}
              shippingInfo={shippingInfo} />
            {SCIDeviceData?.sciDeviceSettings && (
              <SCISettingsTab selectedTab={activeTab}
                SCIDeviceData={SCIDeviceData}
                tabIndex={DeviceEditorTabs.SCI_SETTINGS}
                noUpdate={!SCIUpdate}
                onSubmit={onSubmit}
                serialNumber={populatedDevice?.serialNumber}
                SCILoading={SCILoading}/>
            )}
          </>
        </div>
        <div className={classes.center}>
          {isSCIDevice && !SCIDeviceData && <ContentLoader />}
        </div>
      </div>
    </>
  );
};

export default BulkViewDeviceEditor;
